import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useNurseJobNotificationsQuery } from "../../../../../hooks/useNurseJobNotifications";
import type { Job, Nurse } from "../../../../../utils/models";
import { orderJobChips } from "./jobChipsView";
import { resolveDraftSilent } from "./jobNotificationDefaults";
import type { JobNotificationDraft } from "./useJobNotificationDraft";

/** `isAdded`: not saved on the profile yet. `isNew`: shows the NEW badge, which a new profile skips. */
export type JobEditRow = { id: string; displayName: string; isPrimary: boolean; isAdded: boolean; isNew: boolean; isSilent: boolean | undefined };

export type JobToggleSurface = "edit_table" | "remove_dialog";

type Params = {
	departmentId: string;
	nurse: Nurse | undefined;
	setNurseData: Dispatch<SetStateAction<Nurse>>;
	draft: JobNotificationDraft;
	selectedJobIds: string[];
	availableJobs: Job[];
	onJobsChange: (jobIds: string[]) => void;
};

/**
 * Edit-mode Job field state: the table rows and the remove confirmation.
 * A job unticked in the dropdown is confirmed after the dropdown closes, so the dialog never opens over it.
 */
export const useJobsEditState = ({ departmentId, nurse, setNurseData, draft, selectedJobIds, availableJobs, onJobsChange }: Params) => {
	const { track } = useAnalytics();
	const savedNurseId = nurse?.id && nurse.id > 0 ? nurse.id : undefined;
	const { silentByJobId, isLoading, isError } = useNurseJobNotificationsQuery(departmentId, savedNurseId);
	const [removalQueue, setRemovalQueue] = useState<string[]>([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const jobIdsAtOpen = useRef<string[] | null>(null);

	const rows = useMemo<JobEditRow[]>(() => {
		const context = { originalJobIds: draft.originalJobIds, finalJobIds: selectedJobIds, loadedSilentByJobId: silentByJobId, overrides: draft.overrides };
		const silentByDraft = new Map(
			selectedJobIds.flatMap((id) => {
				const isSilent = resolveDraftSilent(id, context);
				return isSilent === undefined ? [] : [[id, isSilent] as const];
			}),
		);
		const named = selectedJobIds.map((id) => ({
			id,
			// DEFAULT: an empty name for one render when neither job list knows the job yet, instead of a raw id.
			displayName: availableJobs.find((job) => job.id === id)?.displayName ?? nurse?.jobs?.find((job) => job.id === id)?.displayName ?? "",
		}));
		// A new profile has nothing saved to compare to, so every row would be marked "NEW".
		const markNew = savedNurseId !== undefined;
		return orderJobChips(named, selectedJobIds[0], silentByDraft).map((row) => {
			const isAdded = !draft.originalJobIds.includes(row.id);
			return { ...row, isAdded, isNew: markNew && isAdded };
		});
	}, [draft, selectedJobIds, silentByJobId, availableJobs, nurse?.jobs, savedNurseId]);

	const setRowSilent = useCallback(
		(jobId: string, isSilent: boolean, surface: JobToggleSurface) => {
			track("profile_job_notification_toggle", { nurse_id: savedNurseId, job_id: jobId, is_silent: isSilent, surface });
			draft.setOverride(jobId, isSilent);
		},
		[draft, savedNurseId, track],
	);

	/** A job added in this edit was never saved, so it is removed without a confirmation. */
	const isAddedInThisEdit = useCallback((jobId: string) => !draft.originalJobIds.includes(jobId), [draft.originalJobIds]);

	const requestRemove = useCallback(
		(jobId: string) => {
			if (isAddedInThisEdit(jobId)) {
				onJobsChange(selectedJobIds.filter((id) => id !== jobId));
				return;
			}
			setRemovalQueue((queue) => (queue.includes(jobId) ? queue : [...queue, jobId]));
		},
		[isAddedInThisEdit, onJobsChange, selectedJobIds],
	);
	const dequeue = useCallback((jobId: string) => setRemovalQueue((queue) => queue.filter((id) => id !== jobId)), []);

	const onDropdownOpen = useCallback(() => {
		jobIdsAtOpen.current = selectedJobIds;
		setIsDropdownOpen(true);
	}, [selectedJobIds]);
	const onDropdownClose = useCallback(() => setIsDropdownOpen(false), []);

	// Runs after the close re-render, so the dropdown's own restore has landed first.
	// Jobs the employee already held go back in place and wait for confirmation. Jobs added in this edit stay removed.
	useEffect(() => {
		const atOpen = jobIdsAtOpen.current;
		if (isDropdownOpen || !atOpen) return;
		jobIdsAtOpen.current = null;
		const removedHeld = atOpen.filter((id) => !selectedJobIds.includes(id) && !isAddedInThisEdit(id));
		if (removedHeld.length === 0) return;
		const restored = [
			...atOpen.filter((id) => selectedJobIds.includes(id) || removedHeld.includes(id)),
			...selectedJobIds.filter((id) => !atOpen.includes(id)),
		];
		setNurseData((prev) => ({ ...prev, jobIds: restored }));
		setRemovalQueue((queue) => [...queue, ...removedHeld.filter((id) => !queue.includes(id))]);
	}, [isDropdownOpen, selectedJobIds, setNurseData, isAddedInThisEdit]);

	const pendingRemovalId = removalQueue.find((id) => selectedJobIds.includes(id));

	const confirmRemove = useCallback(
		(jobId: string) => {
			onJobsChange(selectedJobIds.filter((id) => id !== jobId));
			dequeue(jobId);
		},
		[onJobsChange, selectedJobIds, dequeue],
	);

	const setPrimaryAndRemove = useCallback(
		(newPrimaryId: string, jobId: string) => {
			setNurseData((prev) => ({ ...prev, jobIds: [newPrimaryId, ...selectedJobIds.filter((id) => id !== newPrimaryId && id !== jobId)] }));
			track("profile_primary_job_change", { primary_job_id: newPrimaryId, nurse_id: savedNurseId, removed_job_id: jobId });
			dequeue(jobId);
		},
		[setNurseData, selectedJobIds, track, savedNurseId, dequeue],
	);

	const muteInstead = useCallback(
		(jobId: string) => {
			track("profile_job_remove_mute_instead_click", { nurse_id: savedNurseId, job_id: jobId });
			setRowSilent(jobId, true, "remove_dialog");
			dequeue(jobId);
		},
		[track, savedNurseId, setRowSilent, dequeue],
	);

	return {
		rows,
		isLoadingNotifications: isLoading,
		isNotificationsError: isError,
		setRowSilent,
		requestRemove,
		pendingRemoval: rows.find((row) => row.id === pendingRemovalId),
		cancelRemove: dequeue,
		confirmRemove,
		setPrimaryAndRemove,
		muteInstead,
		onDropdownOpen,
		onDropdownClose,
	};
};

export type JobsEditState = ReturnType<typeof useJobsEditState>;
